import React from "react";

/**
 * Colorful category-chip strip. Meant to sit right under the free-text
 * search box on each app landing page. Max 10 chips.
 *
 * Props:
 *  - chips: [{ id, label, icon: LucideIcon, color: '#hex', bg: '#hex' }]
 *  - active: chip.id currently selected (or null)
 *  - onChange: (chipId | null) => void  — pass null to clear
 *  - testIdPrefix: string
 */
export default function AppCategoryChips({ chips, active, onChange, testIdPrefix }) {
  if (!chips?.length) return null;
  return (
    <div
      className="mt-4 -mx-1 flex gap-2 overflow-x-auto no-scrollbar pb-2"
      data-testid={`${testIdPrefix}-chips`}
    >
      {chips.map((c) => {
        const Icon = c.icon;
        const isActive = active === c.id;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(isActive ? null : c.id)}
            className={`shrink-0 group inline-flex flex-col items-center gap-1.5 px-2 py-1 rounded-2xl transition-all ${
              isActive ? "scale-105" : "hover:scale-105"
            }`}
            data-testid={`${testIdPrefix}-chip-${c.id}`}
            aria-pressed={isActive}
          >
            <span
              className={`h-14 w-14 rounded-2xl flex items-center justify-center shadow-md transition-shadow ${
                isActive ? "ring-4 ring-white/70 shadow-lg" : "group-hover:shadow-lg"
              }`}
              style={{ background: c.bg }}
            >
              <Icon className="h-7 w-7" style={{ color: c.color }} />
            </span>
            <span
              className={`text-[11px] font-medium text-center leading-tight max-w-[76px] whitespace-normal text-white ${
                isActive ? "" : "opacity-95"
              }`}
              style={{ textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}
            >
              {c.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
